import React, { useState, useEffect } from 'react';
import { X, Send, AlertCircle, MapPin, Box } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from './axiosConfig';
import toast from 'react-hot-toast';

const IncidentModal = ({ isOpen, onClose, initialData = {} }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  
  // نستخدم useEffect لتحديث البيانات لو المودال اتفتح ببيانات جديدة
  const [formData, setFormData] = useState({
    description: '',
    long_description: '',
    priority: 3,
    // نضمن إرسال null لو الحقل فاضي عشان الداتابيز
    assetnum: initialData.assetnum || null, 
    location_id: initialData.location_id || null,
    site_id: initialData.site_id || 'BEDFORD',
    org_id: initialData.org_id || 'EAGLE'
  });

  // تحديث الفورم داخلياً لو الـ initialData اتغيرت (مهم جداً)
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        assetnum: initialData.assetnum || null,
        location_id: initialData.location_id || null,
        site_id: initialData.site_id || 'BEDFORD',
        org_id: initialData.org_id || 'EAGLE'
      }));
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // تنظيف البيانات قبل الإرسال (التحويل لـ null)
    const payload = {
      ...formData,
      location_id: formData.location_id === "" ? null : formData.location_id,
      assetnum: formData.assetnum === "" ? null : formData.assetnum
    };

    try {
      await api.post('/incidents/create', payload);
      toast.success(t('incidentModal.successMsg'));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('incidentModal.failMsg'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-[500px] rounded-lg shadow-2xl overflow-hidden animate-in zoom-in duration-200 border border-white/20">
        {/* Header */}
        <div className="bg-[#005495] text-white p-4 flex justify-between items-center">
          <h2 className="font-black flex items-center gap-2 uppercase tracking-tighter text-sm">
            <AlertCircle size={18} className="text-orange-400" /> {t('incidentModal.createTitle')}
          </h2>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition-colors"><X size={20}/></button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('incidentModal.summaryDescription')}</label>
            <input
              required
              placeholder={t('incidentModal.summaryPlaceholder')}
              className="border-b-2 border-gray-100 focus:border-blue-600 outline-none py-2 font-bold text-gray-700 transition-all"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('incidentModal.priority')}</label>
              <select
                className="border border-gray-200 rounded p-1.5 font-bold text-gray-700 outline-none focus:ring-1 focus:ring-blue-500"
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
              >
                <option value="1">{t('incidentModal.priorityEmergency')}</option>
                <option value="2">{t('incidentModal.priorityHigh')}</option>
                <option value="3">{t('incidentModal.priorityNormal')}</option>
                <option value="4">{t('incidentModal.priorityLow')}</option>
              </select>
            </div>

            {/* 🎯 المنطق الذكي لعرض السياق (Context) */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">{t('incidentModal.contextInfo')}</label>
              <div className="flex justify-end items-center gap-2 mt-1">
                {initialData.assetnum ? (
                  <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-100 font-black text-[10px]">
                    <Box size={12}/> {initialData.assetnum}
                  </div>
                ) : initialData.location_id ? (
                  <div className="flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-1 rounded border border-orange-100 font-black text-[10px]">
                    <MapPin size={12}/> {initialData.location_id}
                  </div>
                ) : (
                  <span className="text-gray-400 italic text-[10px]">{t('incidentModal.generalIncident')}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('incidentModal.detailedDetails')}</label>
            <textarea
              rows="3"
              placeholder={t('incidentModal.detailsPlaceholder')}
              className="border border-gray-100 p-3 rounded outline-none focus:border-blue-600 bg-gray-50 text-[11px] font-medium transition-all"
              value={formData.long_description}
              onChange={(e) => setFormData({...formData, long_description: e.target.value})}
            />
          </div>

          <div className="pt-4 flex gap-2">
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 bg-[#005495] text-white py-2.5 rounded font-black hover:bg-[#1a3353] transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 disabled:opacity-50"
            >
              {loading ? <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"/> : <Send size={16}/>}
              {t('incidentModal.submitTicket')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncidentModal;